import { LEG_MARKERS } from './legTypes'
import { collectTradeIdeas, isTradeMark, toTradeMark } from './tradeIdeas'
import { parseWorkbenchState, SAVED_STATE_VERSIONS, type SymbolSliceState, type WorkbenchSavedState } from './workbenchState'

/**
 * Carrying a chart setup from one strategy to another, through a file.
 *
 * Drawing the same trade ideas again on every new strategy is the tax this
 * removes: the right-click menu writes the current setup to JSON and reads one
 * back. Everything here is pure — no DOM, no chart — so the rules are testable
 * on their own; `downloadTransferFile` is the one exception and touches nothing
 * but an anchor.
 *
 * Validation is NOT re-implemented: an imported file goes through
 * `parseWorkbenchState`, the same hardened parser the mount path runs on the
 * blob coming back from the server. It drops corrupt slices individually,
 * migrates v1 and returns null rather than throwing.
 */

export const TRANSFER_KIND = 'algobuilder/workbench'

/** Envelope version. Bumped only when the envelope itself changes — the state inside carries its own `v`. */
export const TRANSFER_VERSION = 1

export interface TransferSource {
    bid: string
    title?: string
    activeSymbol: string
}

export interface WorkbenchTransferFile {
    kind: typeof TRANSFER_KIND
    v: number
    exportedAt: number
    source: TransferSource
    state: WorkbenchSavedState
}

export type TransferRejection =
    | 'not-json'
    | 'not-workbench'
    | 'unsupported-version'
    | 'unreadable-state'

export type ReadTransferResult =
    | { ok: true; file: WorkbenchTransferFile }
    | { ok: false; reason: TransferRejection }

export interface SliceSummary {
    symbol: string
    ideas: number
    drawings: number
    notes: number
    indicators: number
    /** True when the target already has a setup for this symbol, which the import overwrites. */
    replaces: boolean
    /** True when the account cannot chart this symbol, so the slice is dropped. */
    unavailable: boolean
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * The per-bot AI bookkeeping, dropped on the way out.
 *
 * `aiPush` is the diff base for what the chat has already been shown and
 * `staged` is a context queued for the next message. Carried into another bot
 * they would state that these drawings had already been sent, and that bot's
 * AI would never see them — the exact opposite of why the setup was imported.
 */
function stripBotBookkeeping(state: WorkbenchSavedState): WorkbenchSavedState {
    const bySymbol: Record<string, SymbolSliceState> = {}
    for (const [symbol, slice] of Object.entries(state.bySymbol)) {
        const copy = { ...slice }
        delete copy.aiPush
        bySymbol[symbol] = copy
    }
    const top = { ...state, bySymbol }
    delete top.staged
    return top
}

/** Wrap the current workbench state for export. */
export function buildTransferFile(
    state: WorkbenchSavedState,
    source: TransferSource,
    exportedAt: number = Date.now(),
): WorkbenchTransferFile {
    return {
        kind: TRANSFER_KIND,
        v: TRANSFER_VERSION,
        exportedAt,
        source,
        state: stripBotBookkeeping(state),
    }
}

/**
 * Read a file's text into a validated transfer.
 *
 * A bare saved blob is accepted too, not just an envelope: the dev-only "Show
 * State" dialog copies exactly that to the clipboard, and refusing it would be
 * pedantry about the wrapper rather than about the content.
 */
export function readTransferFile(text: string): ReadTransferResult {
    let raw: unknown
    try {
        raw = JSON.parse(text)
    } catch {
        return { ok: false, reason: 'not-json' }
    }
    if (!isRecord(raw)) return { ok: false, reason: 'not-workbench' }

    const enveloped = raw.kind === TRANSFER_KIND
    if (enveloped && typeof raw.v === 'number' && raw.v > TRANSFER_VERSION) {
        return { ok: false, reason: 'unsupported-version' }
    }
    // An envelope names its payload; anything else is tried as a saved blob.
    const rawState = enveloped ? raw.state : raw
    if (!enveloped && !SAVED_STATE_VERSIONS.includes(raw.v as number)) return { ok: false, reason: 'not-workbench' }

    const state = parseWorkbenchState(rawState)
    if (!state) return { ok: false, reason: enveloped ? 'unreadable-state' : 'not-workbench' }

    const source = isRecord(raw.source) ? raw.source : {}
    return {
        ok: true,
        file: {
            kind: TRANSFER_KIND,
            v: enveloped && typeof raw.v === 'number' ? raw.v : TRANSFER_VERSION,
            exportedAt: typeof raw.exportedAt === 'number' ? raw.exportedAt : 0,
            source: {
                bid: typeof source.bid === 'string' ? source.bid : '',
                ...(typeof source.title === 'string' ? { title: source.title } : {}),
                activeSymbol: state.activeSymbol,
            },
            state: stripBotBookkeeping(state),
        },
    }
}

/**
 * Count a slice the way a trader would read it.
 *
 * Locked connectors are chrome, and a trade idea's own marks are counted once
 * as the idea — listing "1 trade idea, 4 drawings" for a three-mark idea next
 * to one line would overstate the chart the reader is about to overwrite.
 */
function countSlice(slice: SymbolSliceState): { ideas: number; drawings: number } {
    const real = slice.drawings.filter(
        (d) => !d.lock && (d.extendData as { connector?: unknown } | undefined)?.connector !== true,
    )
    const marks = real
        .map((d, i) => toTradeMark(`d${i}`, d.name, d.extendData, d.points[0]))
        .filter((m): m is NonNullable<typeof m> => m !== null)
    return {
        ideas: collectTradeIdeas(marks).length,
        drawings: real.filter((d) => !isTradeMark(d.name) && !LEG_MARKERS.includes(d.name)).length,
    }
}

/**
 * What the import would do, symbol by symbol — the text of the confirmation.
 * Ordered with the file's active symbol first, since that is the one the chart
 * will land on.
 */
export function describeSlices(
    state: WorkbenchSavedState,
    current: Record<string, SymbolSliceState>,
    knownSymbols: Set<string>,
): SliceSummary[] {
    return Object.entries(state.bySymbol)
        .map(([symbol, slice]) => ({
            symbol,
            ...countSlice(slice),
            notes: slice.notes.filter((n) => n.text.trim()).length,
            indicators: slice.indicators.length,
            replaces: Object.prototype.hasOwnProperty.call(current, symbol),
            unavailable: !knownSymbols.has(symbol),
        }))
        .sort((a, b) => {
            if (a.symbol === state.activeSymbol) return -1
            if (b.symbol === state.activeSymbol) return 1
            return a.symbol.localeCompare(b.symbol)
        })
}

/**
 * Fold the imported slices into the current map.
 *
 * Replace, not merge: a symbol named in the file has its slice swapped whole,
 * and a symbol absent from the file is left exactly as it was. Replacing whole
 * slices is also what keeps ids safe — `ideaId`, `ctxId`, `ideaSeq` and note
 * ids cannot collide with something that no longer exists.
 *
 * A symbol this account cannot chart is dropped, the same rule the mount
 * restore applies to a delisted market: a slice whose candles will never load
 * would silently do nothing.
 */
export function mergeImportedSlices(
    current: Record<string, SymbolSliceState>,
    incoming: Record<string, SymbolSliceState>,
    knownSymbols: Set<string>,
    nowMs: number,
): { bySymbol: Record<string, SymbolSliceState>; imported: string[]; dropped: string[] } {
    const bySymbol = { ...current }
    const imported: string[] = []
    const dropped: string[] = []

    for (const [symbol, slice] of Object.entries(incoming)) {
        if (!knownSymbols.has(symbol)) {
            dropped.push(symbol)
            continue
        }
        // Re-stamped: the imported setup is being looked at now, and the LRU
        // that evicts slices past the cap orders by this.
        bySymbol[symbol] = { ...slice, touchedAt: nowMs }
        imported.push(symbol)
    }

    return { bySymbol, imported, dropped }
}

/** Which symbol the chart should land on: the file's own, else the best of what survived. */
export function targetSymbolFor(
    state: WorkbenchSavedState,
    imported: string[],
): string | null {
    if (imported.includes(state.activeSymbol)) return state.activeSymbol
    return imported[0] ?? null
}

const pad = (n: number): string => String(n).padStart(2, '0')

/** `EURUSD-H4-workbench-20260920-1732.json` — sorts by market, then by when it was taken. */
export function transferFileName(symbol: string, timeframe: string, at: Date = new Date()): string {
    const stamp =
        `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}`
        + `-${pad(at.getHours())}${pad(at.getMinutes())}`
    const safe = (s: string) => s.replace(/[^A-Za-z0-9._-]/g, '') || 'chart'
    return `${safe(symbol)}-${safe(timeframe)}-workbench-${stamp}.json`
}

/**
 * Hand the file to the browser. Object URL rather than a data URL: a setup with
 * many drawings can run to hundreds of kilobytes, which some browsers refuse to
 * navigate to as a data URI.
 */
export function downloadTransferFile(file: WorkbenchTransferFile, fileName: string): void {
    const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    try {
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
    } finally {
        // A revoke in the same tick can beat the download in some browsers.
        setTimeout(() => URL.revokeObjectURL(url), 0)
    }
}
